import React, { useMemo, useRef, useState } from 'react';

export type PickerBadge = {
    label: string;
    tone?: 'slate' | 'blue' | 'green' | 'amber' | 'red' | 'purple';
};

export type PickerOption<TValue extends string = string> = {
    value: TValue;
    label: string;
    description?: string;
    badges?: PickerBadge[];
    colorToken?: string;
};

type EntityTypeaheadPickerProps<TValue extends string = string> = {
    value?: TValue;
    options: PickerOption<TValue>[];
    placeholder?: string;
    emptyText?: string;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    disabled?: boolean;
    loading?: boolean;
    onChange: (value: TValue) => void;
    onQueryChange?: (query: string) => void;
};

const toneClassMap: Record<NonNullable<PickerBadge['tone']>, string> = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
};

const colorTokenClassMap: Record<string, string> = {
    'so-draft': 'border-l-4 border-slate-300',
    'so-confirmed': 'border-l-4 border-blue-400',
    'so-production': 'border-l-4 border-indigo-400',
    'so-ready': 'border-l-4 border-cyan-400',
    'so-dispatched': 'border-l-4 border-emerald-400',
    'so-closed': 'border-l-4 border-green-500',
    'so-cancelled': 'border-l-4 border-red-400',
    'wo-planned': 'border-l-4 border-slate-300',
    'wo-in-progress': 'border-l-4 border-amber-400',
    'wo-completed': 'border-l-4 border-emerald-400',
    'wo-cancelled': 'border-l-4 border-red-400',
};

export function EntityTypeaheadPicker<TValue extends string = string>({
    value,
    options,
    placeholder = 'Search...',
    emptyText = 'No matching options',
    menuPlacement = 'auto',
    disabled,
    loading,
    onChange,
    onQueryChange,
}: EntityTypeaheadPickerProps<TValue>) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const selected = useMemo(() => options.find((opt) => opt.value === value), [options, value]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((opt) => {
            const haystack = `${opt.label} ${opt.description || ''} ${(opt.badges || []).map((b) => b.label).join(' ')}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [options, query]);

    const resolvedMenuPlacement = useMemo<'bottom' | 'top'>(() => {
        if (menuPlacement === 'top' || menuPlacement === 'bottom') {
            return menuPlacement;
        }
        if (!open || !wrapperRef.current || typeof window === 'undefined') {
            return 'bottom';
        }
        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const estimatedMenuHeight = 320;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
            return 'top';
        }
        return 'bottom';
    }, [menuPlacement, open, filtered.length]);

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                type="button"
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-left"
                onClick={() => {
                    if (disabled) return;
                    setOpen((prev) => !prev);
                }}
                disabled={disabled}
            >
                {selected ? selected.label : <span className="text-muted-foreground">{placeholder}</span>}
            </button>

            {open && (
                <div
                    className={`absolute z-50 w-full rounded-md border bg-white shadow-lg ${
                        resolvedMenuPlacement === 'top' ? 'bottom-full mb-1' : 'mt-1'
                    }`}
                >
                    <div className="p-2 border-b">
                        <input
                            autoFocus
                            value={query}
                            placeholder={placeholder}
                            onChange={(event) => {
                                const next = event.target.value;
                                setQuery(next);
                                if (onQueryChange) onQueryChange(next);
                            }}
                            className="w-full h-9 rounded-md border px-2 text-sm"
                        />
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                        {loading ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">Loading...</div>
                        ) : filtered.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">{emptyText}</div>
                        ) : (
                            filtered.map((opt) => {
                                const markerClass = colorTokenClassMap[opt.colorToken || ''] || '';
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`w-full rounded px-3 py-2 text-left hover:bg-slate-50 ${markerClass}`}
                                        onClick={() => {
                                            onChange(opt.value);
                                            setOpen(false);
                                            setQuery('');
                                        }}
                                    >
                                        <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                                        {opt.description ? <div className="text-xs text-slate-500">{opt.description}</div> : null}
                                        {opt.badges && opt.badges.length > 0 ? (
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {opt.badges.map((badge, idx) => (
                                                    <span
                                                        key={`${opt.value}-${idx}-${badge.label}`}
                                                        className={`inline-flex rounded px-2 py-0.5 text-[11px] font-medium ${toneClassMap[badge.tone || 'slate']}`}
                                                    >
                                                        {badge.label}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : null}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default EntityTypeaheadPicker;
